// useLauncherPosition — where the launcher sits, and where the card goes to
// follow it.
//
// The launcher used to be pinned to the bottom-right corner, and the card, the
// tip and the table's pagination reserve were all derived from that one fact.
// Letting a person drag it means the card has to be derived from a position
// instead, and the launcher and the dock (two sibling components) have to agree
// on it, hence a small module-level store rather than component state.
//
// ─── THE POSITION IS ANCHORED TO THE NEAREST SIDE ──────────────────────────
// `{ side, inset, bottom }`: the distance from the left OR right edge, whichever
// the launcher's centre is closer to, and from the bottom. Anchoring to the near
// side matters because the pill expands sideways when it is hovered: a pill
// pinned by its right edge at the far left of the screen would grow off-screen.
// Anchored to the near side it always grows into the page.
//
// Everything is clamped to the viewport on read, so a position saved on a big
// monitor can never leave the button off-screen on a small window, and resizing
// the window pulls it back in.
//
// ─── THE CARD FOLLOWS, AND NEVER COVERS THE BUTTON ─────────────────────────
// `cardPlacement` puts the card above the launcher when there is room for at
// least the card's minimum height, below it otherwise, and falls back to the
// default corner on a window too short for either. Horizontally it lines up with
// the launcher's side and is pulled in so it stays fully on screen.
//
// Persisted in the LOCAL namespace like the rest of the launcher's preferences:
// a layout choice that is forgotten every tab would be a nag, not a feature.

import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';

import { createStorage } from '@fluxora/storage';

const store = createStorage('ai-assistant', { backend: 'local' });
const KEY = 'launcher-position';

/** Mirrors `--crm-ai-edge`, `--crm-ai-launcher`, `--crm-ai-launcher-gap` in crm.css. */
export const EDGE_PX = 20;
export const LAUNCHER_HEIGHT_PX = 48;
export const LAUNCHER_GAP_PX = 12;
/** The widest the pill gets (`--crm-ai-launcher-w`), used to keep it on screen. */
export const LAUNCHER_MAX_WIDTH_PX = 160;
/** Mirrors `--crm-ai-h-min` / `--crm-ai-h`. */
export const CARD_MIN_HEIGHT_PX = 340;
export const CARD_HEIGHT_PX = 660;
/** Mirrors `--crm-ai-w` and `--crm-ai-w-wide` (`min(720px, 46vw)`). */
export const CARD_WIDTH_PX = 420;

export type LauncherSide = 'left' | 'right';

export interface LauncherPosition {
  readonly side: LauncherSide;
  /** Distance from the `side` edge, in px. */
  readonly inset: number;
  /** Distance from the bottom edge, in px. */
  readonly bottom: number;
}

export interface Viewport {
  readonly width: number;
  readonly height: number;
}

/** Where it sits before anyone has moved it: the original bottom-right corner. */
export const DEFAULT_POSITION: LauncherPosition = {
  side: 'right',
  inset: EDGE_PX,
  bottom: EDGE_PX,
};

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), Math.max(min, max));
}

/** Pull a position inside the viewport. Pure, so it is testable without a DOM. */
export function clampPosition(position: LauncherPosition, viewport: Viewport): LauncherPosition {
  return {
    side: position.side,
    inset: clamp(position.inset, EDGE_PX, viewport.width - LAUNCHER_MAX_WIDTH_PX - EDGE_PX),
    bottom: clamp(position.bottom, EDGE_PX, viewport.height - LAUNCHER_HEIGHT_PX - EDGE_PX),
  };
}

/**
 * From where the launcher's box ended up on screen to a position. The side is
 * whichever edge the box's centre is nearer to.
 */
export function positionFromRect(
  rect: {
    readonly left: number;
    readonly width: number;
    readonly top: number;
    readonly height: number;
  },
  viewport: Viewport,
): LauncherPosition {
  const centreX = rect.left + rect.width / 2;
  const side: LauncherSide = centreX < viewport.width / 2 ? 'left' : 'right';
  const inset = side === 'left' ? rect.left : viewport.width - (rect.left + rect.width);
  const bottom = viewport.height - (rect.top + rect.height);
  return clampPosition({ side, inset, bottom }, viewport);
}

export interface CardPlacement {
  readonly side: LauncherSide;
  readonly inset: number;
  readonly vertical: 'above' | 'below';
  /** Distance from the bottom edge (when `above`) or the top edge (when `below`). */
  readonly offset: number;
  readonly height: number;
}

/**
 * Where the floating card goes for a launcher position, or `null` when the
 * window is too short for the card either side of the launcher. `null` means
 * "use the stylesheet's default corner", which is the old behaviour.
 */
export function cardPlacement(
  position: LauncherPosition,
  cardWidth: number,
  viewport: Viewport,
): CardPlacement | null {
  const inset = clamp(position.inset, EDGE_PX, viewport.width - cardWidth - EDGE_PX);

  const aboveOffset = position.bottom + LAUNCHER_HEIGHT_PX + LAUNCHER_GAP_PX;
  const aboveRoom = viewport.height - aboveOffset - EDGE_PX;
  if (aboveRoom >= CARD_MIN_HEIGHT_PX) {
    return {
      side: position.side,
      inset,
      vertical: 'above',
      offset: aboveOffset,
      height: Math.min(aboveRoom, CARD_HEIGHT_PX),
    };
  }

  const belowOffset = viewport.height - position.bottom + LAUNCHER_GAP_PX;
  const belowRoom = viewport.height - belowOffset - EDGE_PX;
  if (belowRoom >= CARD_MIN_HEIGHT_PX) {
    return {
      side: position.side,
      inset,
      vertical: 'below',
      offset: belowOffset,
      height: Math.min(belowRoom, CARD_HEIGHT_PX),
    };
  }
  return null;
}

/** True when the tip should open BELOW the launcher because there is no room above. */
export function hintBelow(position: LauncherPosition, viewport: Viewport): boolean {
  const roomAbove = viewport.height - position.bottom - LAUNCHER_HEIGHT_PX;
  return roomAbove < 120;
}

// ── persistence + shared store ──────────────────────────────────────────────

function isPosition(raw: unknown): raw is LauncherPosition {
  if (typeof raw !== 'object' || raw === null) return false;
  const candidate = raw as Partial<LauncherPosition>;
  return (
    (candidate.side === 'left' || candidate.side === 'right') &&
    typeof candidate.inset === 'number' &&
    Number.isFinite(candidate.inset) &&
    typeof candidate.bottom === 'number' &&
    Number.isFinite(candidate.bottom)
  );
}

function load(): LauncherPosition {
  const raw: unknown = store.get<unknown>(KEY);
  return isPosition(raw) ? raw : DEFAULT_POSITION;
}

let current: LauncherPosition = load();
const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): LauncherPosition {
  return current;
}

function publish(next: LauncherPosition, persist: boolean): void {
  current = next;
  if (persist) {
    try {
      store.set(KEY, next);
    } catch {
      // Storage full or blocked: the position still holds for this page load.
    }
  }
  listeners.forEach((listener) => listener());
}

/** Test seam: forget any saved position. */
export function resetLauncherPositionForTests(): void {
  current = DEFAULT_POSITION;
  try {
    store.remove(KEY);
  } catch {
    // nothing to reset
  }
  listeners.forEach((listener) => listener());
}

function readViewport(): Viewport {
  return { width: window.innerWidth, height: window.innerHeight };
}

export interface LauncherPositionApi {
  /** The saved position, already pulled inside the current window. */
  readonly position: LauncherPosition;
  readonly viewport: Viewport;
  /** Move without saving (during a drag). */
  readonly moveTo: (next: LauncherPosition) => void;
  /** Move and save (end of a drag, or a keyboard nudge). */
  readonly commit: (next: LauncherPosition) => void;
  /** Shift by a pixel delta, in screen terms: +x is right, +y is down. */
  readonly nudge: (dx: number, dy: number) => void;
}

export function useLauncherPosition(): LauncherPositionApi {
  const saved = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const [viewport, setViewport] = useState<Viewport>(readViewport);

  useEffect(() => {
    const onResize = (): void => setViewport(readViewport());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const position = useMemo(() => clampPosition(saved, viewport), [saved, viewport]);

  const moveTo = useCallback((next: LauncherPosition): void => publish(next, false), []);
  const commit = useCallback((next: LauncherPosition): void => publish(next, true), []);

  const nudge = useCallback(
    (dx: number, dy: number): void => {
      // Work in screen coordinates, then re-derive the side so a nudge across the
      // middle flips the anchor like a drag does.
      const left =
        position.side === 'left'
          ? position.inset
          : viewport.width - position.inset - LAUNCHER_MAX_WIDTH_PX;
      const top = viewport.height - position.bottom - LAUNCHER_HEIGHT_PX;
      publish(
        positionFromRect(
          {
            left: left + dx,
            width: LAUNCHER_MAX_WIDTH_PX,
            top: top + dy,
            height: LAUNCHER_HEIGHT_PX,
          },
          viewport,
        ),
        true,
      );
    },
    [position, viewport],
  );

  return { position, viewport, moveTo, commit, nudge };
}

/** What the dock needs: the card's placement for the current launcher position. */
export function useCardPlacement(cardWidth: number): CardPlacement | null {
  const { position, viewport } = useLauncherPosition();
  return useMemo(
    () => cardPlacement(position, cardWidth, viewport),
    [position, cardWidth, viewport],
  );
}
